import { useEffect, useState } from 'react'
import styles from './styles/App.module.css';
import DesktopNavbar from 'components/navigation/DesktopNavbar';
import MobileNavbar from 'components/navigation/MobileNavbar';
import Home from './components/mainContent/Home';
import About from './components/mainContent/Career';
import Projects from './components/mainContent/Projects';
import { Helmet } from 'react-helmet';
import { NavigationLayer, MainContentLayer } from './components/LayerWrapper';
import { siteTitle } from './data/personal';

const App = () => {
  const [currentSection, setCurrentSection] = useState('home');
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Prevent browser from restoring scroll position
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Always scroll to top on reload
    window.scrollTo(0, 0);
  }, []);

  // Note: Color injection is now handled by useTheme hook in navbar components

  // Update which navbar to use based on screen sizes
  useEffect(() => {
    // Function to update the main content size and navbar size based on screen size
    const updateContentOnScreenSize = () => {
      // Get the window size.
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // Update the main content padding, sizing, etc.
      // If the screen is less than 768px, use the mobile navbar.
      // The Navbar is on the top of the screen.
      if (screenWidth < 768) {

        // Update the size of the navbar elements.
        // If the scree is less than 768 pixels tall, then the navbar is 40 pixels.
        if (screenHeight < 768) 
        {
          // The main content box size.
          document.documentElement.style.setProperty('--mainContent-margin-top', `50px`);
          document.documentElement.style.setProperty('--mainContent-margin-left', `0vw`);
          document.documentElement.style.setProperty('--mainContent-width', `100vw`);

          // The navbar size.
          document.documentElement.style.setProperty('--navbar-width', `100vw`);
          document.documentElement.style.setProperty('--navbar-height', `50px`);

          return;
        }

        // Update the size of navbar elements.
        // If the screen is greater than 768 pixels tall, then the navbar is 6.5vh.
        else 
        {
          // The main content box size.
          document.documentElement.style.setProperty('--mainContent-margin-top', `6.5vh`);
          document.documentElement.style.setProperty('--mainContent-margin-left', `0vw`);
          document.documentElement.style.setProperty('--mainContent-width', `100vw`);

          // The navbar size.
          document.documentElement.style.setProperty('--navbar-width', `100vw`);
          document.documentElement.style.setProperty('--navbar-height', `6.5vh`);

          return;
        }
      } 
      // If the screen is greater than 768px, use the desktop navbar.
      // The Navbar is on the left side of the screen.
      else 
      {
        // The main content box size.
        document.documentElement.style.setProperty('--mainContent-margin-top', `0vh`);
        document.documentElement.style.setProperty('--mainContent-margin-left', `20vw`);
        document.documentElement.style.setProperty('--mainContent-width', `80vw`);

        // The navbar size.
        document.documentElement.style.setProperty('--navbar-width', `20vw`);
        document.documentElement.style.setProperty('--navbar-height', `100vh`);
        return;
      }
    };

    // Function to check screen width
    const checkScreenWidth = () => {
      setIsSmallScreen(window.innerWidth < 768); // Adjust threshold as needed
    };

    // Initial measurement
    updateContentOnScreenSize();
    checkScreenWidth();
    
    // Update on resize
    window.addEventListener('resize', updateContentOnScreenSize);
    window.addEventListener("resize", checkScreenWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateContentOnScreenSize);
      window.removeEventListener("resize", checkScreenWidth);
    };

  }, []);

  // Add intersection observer to track visible sections
  useEffect(() => {
    const options = {
      root: null, // Use the viewport as the root
      rootMargin: '0px',
      threshold: [0.1, 0.5], // Multiple thresholds - detect when section is just 10% or 50% visible
    };

    // Object to store visibility ratios of all sections
    const sectionVisibility: { [key: string]: number } = {};

    // Callback function when intersection changes
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        // Update the visibility ratio for the section
        sectionVisibility[entry.target.id] = entry.intersectionRatio;
      });

      // Find the section with the highest visibility ratio
      let mostVisibleSection = currentSection;
      let maxVisibility = 0;

      Object.entries(sectionVisibility).forEach(([id, visibility]) => {
        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleSection = id;
        }
      });

      // Update the current section if it has changed
      if (mostVisibleSection !== currentSection) {
        setCurrentSection(mostVisibleSection);
      }
    };

    // Create an observer instance
    const observer = new IntersectionObserver(handleIntersection, options);

    // Target elements to observe - all main sections
    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => {
      observer.observe(section);
      // Initialize visibility to 0 for all sections
      sectionVisibility[section.id] = 0;
    });

    // Cleanup function
    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, [currentSection]); // Add currentSection as a dependency to ensure updates

  return (
    <div className={styles.safeAreaContainer}>
      <div className={styles.app}>
        <Helmet>
          <title>{siteTitle}</title>
          {/* <link rel="preconnect" href="https://fonts.googleapis.com"></link> */}
          {/* <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin></link> */}
          {/* <link href="https://fonts.googleapis.com/css2?family=Titillium+Web:ital,wght@0,200;0,300;0,400;0,600;0,700;0,900;1,200;1,300;1,400;1,600;1,700&display=swap" rel="stylesheet"></link> */}
        </Helmet>
        <NavigationLayer>
          {isSmallScreen ? <MobileNavbar onSectionChange={setCurrentSection} currentSection={currentSection}/> : <DesktopNavbar onSectionChange={setCurrentSection} currentSection={currentSection}/>}
        </NavigationLayer>
        
        <MainContentLayer>
          <main className={styles.mainContent}>
            <Home />
            <Projects isSmallScreen={isSmallScreen} />
            <About />
          </main>
        </MainContentLayer>
      </div>
    </div>
  );
};

export default App
